import { createMetadataFilter, generateNodesAndEdgesFromRoute } from "./HelperFunctions";
import TrailblazerGraphData from "../data/TrailblazerGraphData";

/**
 * This class processes a NetworkRoute to construct a graph representation.
 * It provides listeners with updates when the graph data changes.
 */
export default class GraphDataService {
    constructor() {
        this.graphData = null;
        this.listeners = [];
    }

    /**
     * Processes a NetworkRoute and generates a graph representation.
     */
    processRoute(route) {
        if (!route) return null;

        const { nodes, edges } = generateNodesAndEdgesFromRoute(route);
        this.graphData = new TrailblazerGraphData({ nodes, edges });

        this.notifyListeners();
        return this.graphData;
    }

    /**
     * Returns true if graph data is not null.
     */
    hasGraphData() {
        return this.graphData !== null;
    }

    /**
     * Filters based on a metadata key and value.
     */
    filterByMetadataKeyValuePair(key, values) {
        const valueList = values.split(",").map(v => v.trim());

        // Create a single filter that checks if the node matches ANY of the values
        const combinedMetadataFilter = node => valueList.some((value) => {
            const metadataFilterFunction = createMetadataFilter(key, value);
            return metadataFilterFunction(node);
        });

        // Apply the combined filter once
        this.graphData.applyFilter(combinedMetadataFilter);
        this.notifyListeners();
    }

    /**
     * Resets the graph.
     */
    resetGraph() {
        this.graphData.resetGraphData();
    }

    /**
     * Gets all the elements in the graph data.
     */
    getElements() {
        return this.graphData ? this.graphData.getElements() : [];
    }

    /**
     * Adds the given change listener.
     */
    addChangeListener(listener) {
        this.listeners.push(listener);
    }

    /**
     * Removes the given change listener.
     * @param listener
     */
    removeChangeListener(listener) {
        this.listeners = this.listeners.filter(l => l !== listener);
    }

    /**
     * Notifies listeners of a graph data update.
     */
    notifyListeners() {
        this.listeners.forEach(listener => listener(this.graphData));
    }
}
